import React, { useEffect, useRef } from 'react';
interface PolaroidVideoProps {
  src: string;
  caption: string;
  /** Activa reproducción con sonido cuando el video entra en pantalla. */
  autoSound?: boolean;
  /** Inclinación del marco, como una foto pegada en un expediente. */
  tiltClassName?: string;
}

/** Video en un marco tipo Polaroid ligeramente rotado. */
export function PolaroidVideo({
  src,
  caption,
  autoSound = false,
  tiltClassName = '-rotate-1'
}: PolaroidVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (!autoSound) return;
    const video = videoRef.current;
    if (!video) return;
    let cancelled = false;
    let pendingGesture = false;
    const gestureEvents = ['pointerdown', 'keydown', 'touchstart'] as const;
    function onGesture() {
      const el = videoRef.current;
      if (!el || cancelled) return;
      el.muted = false;
      el.volume = 1;
      void el.play().catch(() => undefined);
      removeGestureListeners();
    }
    function removeGestureListeners() {
      gestureEvents.forEach(event => document.removeEventListener(event, onGesture));
      pendingGesture = false;
    }
    const addGestureListeners = () => {
      if (pendingGesture) return;
      pendingGesture = true;
      gestureEvents.forEach(event => document.addEventListener(event, onGesture, {
        passive: true
      }));
    };
    const playWithSound = () => {
      const el = videoRef.current;
      if (!el || cancelled) return;
      el.muted = false;
      el.volume = 1;
      el.play().catch(() => {
        if (cancelled) return;
        el.muted = true;
        void el.play().catch(() => undefined);
        addGestureListeners();
      });
    };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const el = videoRef.current;
        if (!el) return;
        if (entry.isIntersecting) playWithSound();else el.pause();
      });
    }, {
      threshold: 0.5
    });
    observer.observe(video);
    return () => {
      cancelled = true;
      observer.disconnect();
      removeGestureListeners();
    };
  }, [autoSound]);
  return <figure className={`mx-auto w-full max-w-3xl bg-paper p-3 pb-5 shadow-paper ring-1 ring-ink/20 transition-transform duration-500 hover:rotate-0 sm:p-4 sm:pb-7 ${tiltClassName}`} data-id="element-145" data-mp="ujwjl29s8k2p">
      <div className="flex items-center justify-center overflow-hidden bg-ink" data-id="element-146" data-mp="1v8uevlwxajv">
        <video ref={videoRef} src={src} className="block max-h-[34rem] w-full" controls playsInline loop={autoSound} preload={autoSound ? 'auto' : 'metadata'} data-id="element-147" data-mp="1t5v1kyve52h">
          Tu navegador no admite la reproducción de video.
        </video>
      </div>
      <figcaption className="mt-3 text-center font-folio text-[0.62rem] uppercase tracking-[0.2em] text-ink/70 sm:mt-4" data-id="element-148" data-mp="3fa58k9tw9pyv">
        {caption}
      </figcaption>
    </figure>;
}